import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { useToast } from "../Context/ToastContext";
import jwtInterceptor from "../service/jwtInterceptor";
import { API_URL } from "../utils/constants";
import FaosFilterCard from "../component/FaosFilterCard";
import { Paper, Box, Grid } from '@material-ui/core';
import TablePagination from '@mui/material/TablePagination';
import Table from 'react-bootstrap/Table';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

const tableStyles = {
  th: {
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    backgroundColor: "#fff",
    fontWeight: 500,
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
    padding: "16px"
  },
  td: {
    fontSize: "0.875rem",
    color: "rgba(0, 0, 0, 0.87)",
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
    padding: "16px"
  },
  tr: {
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  },
  expandIcon: {
    cursor: 'pointer',
    color: '#1976d2',
    marginRight: '8px',
    verticalAlign: 'middle'
  }
};

const columns = [
  { accessor: "fao_code_number", show: "N° FAO" },
  { accessor: "type_name", show: "Type" },
  { accessor: "type_code", show: "Code Type" },
  { accessor: "work_description", show: "Description des travaux" },
  { accessor: "work_type", show: "Type de travaux" },
  { accessor: "address", show: "Adresse" },
  { accessor: "city", show: "Commune" },
  { accessor: "region", show: "Canton" },
  { accessor: "architect", show: "Architecte" },
  { accessor: "owner", show: "Propriétaire" },
  { accessor: "result", show: "Résultat" },
  { accessor: "inquiry_start_date", show: "Date" },
  { accessor: "url_official", show: "Lien" },
  { accessor: "classification", show: "Classification" },
  { accessor: "dispensation", show: "Dispense" },
  { accessor: "easement", show: "Servitude" },
  { accessor: "jurisdiction_name", show: "Juridiction" },
  { accessor: "camac_code_number", show: "N° CAMAC" }
];

const FaosContainer = () => {
  const [loading, setLoading] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState({});
  const [filters, setFilters] = useState({
    reference: "",
    title: "",
    municipality: "",
    canton: "",
    status: "",
    dateRange: {
      startDate: null,
      endDate: null
    },
    isEmpty: true
  });

  const toaster = useToast();
  const location = useLocation();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Reset expanded rows when changing page
    setExpandedRows({});
  };

  const fontWeightTitle = {
    fontWeight: "600",
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    // Reset expanded rows when changing rows per page
    setExpandedRows({});
  };

  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  const buildSearchQuery = () => {
    const query = {
      bool: {
        must: []
      }
    };

    // Fonction utilitaire pour créer une recherche flexible
    const createFlexibleSearch = (field, value) => {
      if (!value) return null;
      const cleanedValue = value.trim();
      return {
        bool: {
          should: [
            {
              wildcard: {
                [field]: {
                  value: `*${cleanedValue}*`,
                  case_insensitive: true
                }
              }
            }
          ],
          minimum_should_match: 1
        }
      };
    };

    // Application des filtres avec recherche flexible
    if (filters.reference) {
      query.bool.must.push(createFlexibleSearch("type_name", filters.reference));
    }

    if (filters.type_code) {
      query.bool.must.push(createFlexibleSearch("type_code", filters.type_code));
    }

    if (filters.title) {
      query.bool.must.push(createFlexibleSearch("work_description", filters.title));
    }

    if (filters.municipality) {
      query.bool.must.push(createFlexibleSearch("city", filters.municipality));
    }

    if (filters.canton) {
      query.bool.must.push(createFlexibleSearch("region", filters.canton));
    }

    if (filters.status) {
      query.bool.must.push(createFlexibleSearch("result", filters.status));
    }

    // Ajout des nouveaux filtres pour propriétaire et adresse
    if (filters.owner) {
      query.bool.must.push(createFlexibleSearch("owner", filters.owner));
    }

    if (filters.address) {
      query.bool.must.push(createFlexibleSearch("address", filters.address));
    }

    if (filters.dateRange.startDate || filters.dateRange.endDate) {
      const rangeQuery = {
        range: {
          "inquiry_start_date": {}
        }
      };

      if (filters.dateRange.startDate) {
        rangeQuery.range.inquiry_start_date.gte = new Date(filters.dateRange.startDate.getTime() + 24*60*60*1000).toISOString().split('T')[0];
      }

      if (filters.dateRange.endDate) {
        rangeQuery.range.inquiry_start_date.lte = filters.dateRange.endDate.toISOString().split('T')[0];
      }

      query.bool.must.push(rangeQuery);
    }

    console.log('Built query:', JSON.stringify(query, null, 2));
    return query.bool.must.length > 0 ? query : { match_all: {} };
  };

  const fetchFaos = async () => {
    setLoading(true);
    try {
      const searchQuery = buildSearchQuery();
      console.log('Search query:', JSON.stringify(searchQuery, null, 2));

      const response = await jwtInterceptor.post(
          `${API_URL}/faos/search`,
          {
            query: searchQuery,
            page: page + 1,
            size: rowsPerPage
          }
      );

      console.log('API Response:', response.data);

      if (response.data && response.data.data && response.data.data.results) {
        const results = response.data.data.results;
        console.log('Setting data from results:', results);
        setDataTable(results);
        setTotalCount(response.data.data.total || 0);
      } else {
        console.warn('Unexpected response structure:', response.data);
        setDataTable([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des FAOS:", error);
      toaster.notifyError("Erreur lors de la récupération des FAOS");
      setDataTable([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    console.log("Nouveaux filtres:", newFilters);
    setFilters(newFilters);
    setPage(0);
  };

  useEffect(() => {
    fetchFaos();
  }, [page, rowsPerPage, filters]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Erreur de formatage de date:", error);
      return "-";
    }
  };

  return (
      <Box className="my-4 w-100 px-4">
        <Helmet>
          <title>Enquêtes publiques (FAOS) | MixData</title>
        </Helmet>
        <Grid container spacing={2}>
          <Grid
              className="filters shadow-none"
              xl={3}
              lg={3}
              md={4}
              sm={12}
              xs={12}
          >
            <Paper className="shadow-none" >
              <FaosFilterCard
                  filters={filters}
                  onFilterChange={handleFilterChange}
              />
            </Paper>
          </Grid>
          <Grid xl={9} lg={9} md={8} sm={12} xs={12}>
            <Box className="mb-3 d-flex justify-content-between">
              <h3 style={{...fontWeightTitle}}>Toutes les enquêtes</h3>
            </Box>
            <Box className="listFaos h-auto w-100 m-0 position-relative z-index">
              <Paper
                  className={
                      "py-4 px-4 rounded shadow-none App-container m-0 overflow-hidden position-relative z-index" +
                      " " +
                      " d-block"
                  }
              >
                <Box className="overflow-auto">
                  <Table>
                    <thead
                        style={{
                          background: "#eeeeee",
                          borderBottom: "2px #DEE2E6 solid",
                          fontWeight: "normal",
                          color: "#363636",
                          marginBottom: "5px",
                        }}
                    >
                    <tr className="parcelles-table">
                      {columns.map((field) => (
                          <th key={field.accessor} style={tableStyles.th}>
                            {field.show}
                          </th>
                      ))}
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                          <td colSpan={columns.length} align="center" style={tableStyles.td}>
                            Chargement en cours...
                          </td>
                        </tr>
                    ) : dataTable.length > 0 ? (
                        dataTable.map((faos, index) => {
                          const isExpanded = expandedRows[faos._id] || false;
                          return (
                              <tr
                                  key={faos._id}
                                  style={tableStyles.tr}
                              >
                                <td style={tableStyles.td}>
                              <span
                                  onClick={() => toggleRowExpansion(faos._id)}
                                  style={tableStyles.expandIcon}
                              >
                                {isExpanded ?
                                    <ExpandLessIcon fontSize="small" /> :
                                    <ExpandMoreIcon fontSize="small" />
                                }
                              </span>
                                  {faos._source?.fao_code_number || "-"}
                                </td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.type_name || "-" : (faos._source?.type_name || "-").substring(0, 20) + (faos._source?.type_name?.length > 20 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{faos._source?.type_code || "-"}</td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.work_description || "-" : (faos._source?.work_description || "-").substring(0, 30) + (faos._source?.work_description?.length > 30 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.work_type || "-" : (faos._source?.work_type || "-").substring(0, 20) + (faos._source?.work_type?.length > 20 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.address || "-" : (faos._source?.address || "-").substring(0, 20) + (faos._source?.address?.length > 20 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{faos._source?.city || "-"}</td>
                                <td style={tableStyles.td}>{faos._source?.region || "-"}</td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.architect || "-" : (faos._source?.architect || "-").substring(0, 20) + (faos._source?.architect?.length > 20 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{isExpanded ? faos._source?.owner || "-" : (faos._source?.owner || "-").substring(0, 20) + (faos._source?.owner?.length > 20 ? "..." : "")}</td>
                                <td style={tableStyles.td}>{faos._source?.result || "-"}</td>
                                <td style={tableStyles.td}>{formatDate(faos._source?.inquiry_start_date)}</td>
                                <td style={tableStyles.td}>
                                  {faos._source?.url_official ? (
                                      <a href={faos._source.url_official} target="_blank" rel="noopener noreferrer">
                                        Voir
                                      </a>
                                  ) : "-"}
                                </td>
                                <td style={tableStyles.td}>{faos._source?.classification || "-"}</td>
                                <td style={tableStyles.td}>{faos._source?.dispensation || "-"}</td>
                                <td style={tableStyles.td}>{faos._source?.easement || "-"}</td>
                                <td style={tableStyles.td}>{faos._source?.jurisdiction_name || "-"}</td>
                                <td style={tableStyles.td}>{faos._source?.camac_code_number || "-"}</td>
                              </tr>
                          );
                        })
                    ) : (
                        <tr>
                          <td colSpan={columns.length} align="center" style={tableStyles.td}>
                            Aucune enquête trouvée
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </Table>
                </Box>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Lignes par page"
                    labelDisplayedRows={({from, to, count}) =>
                        `${from}-${to} sur ${count}`
                    }
                />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>
  );
};

export default FaosContainer;